import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    makeStyles,
    tokens,
    Card,
    CardHeader,
    Button,
    Table,
    TableHeader,
    TableRow,
    TableHeaderCell,
    TableBody,
    TableCell,
    Badge,
    Dialog,
    DialogSurface,
    DialogTitle,
    DialogBody,
    DialogActions,
    DialogContent,
    Input,
    Field,
    Spinner,
    Toast,
    ToastTitle,
    ToastBody,
    useToastController,
    Toaster,
    useId,
    Text,
    Tooltip,
    Tab,
    TabList,
    Dropdown,
    Option,
} from '@fluentui/react-components';
import {
    PersonAddRegular,
    DeleteRegular,
    MailRegular,
    ArrowSyncRegular,
    CheckmarkCircleRegular,
    ClockRegular,
    CopyRegular,
    ArrowLeftRegular,
    EditRegular,
    BuildingRegular,
    AddRegular,
    FolderRegular,
} from '@fluentui/react-icons';
import {
    AdminUser,
    listAllUsers,
    createUser,
    deleteUser,
    resendInvite,
    updateUser,
    ClientInfo,
    listClients,
    createClient,
    updateClient,
    deleteClient,
    getAccessCatalog,
} from '../services/api';

const DEFAULT_DOC_TYPES = ['810', '846', '850', '855', '856', '860', '997'];
const DEFAULT_PARTNER_OPTIONS = ['ANDA', 'BOMGAARS', 'LOWES', 'TRUE-VALUE', 'RURAL-KING'];

function splitList(value?: string): string[] {
    if (typeof value !== 'string') return [];
    return value
        .split(',')
        .map(item => item.trim().toUpperCase())
        .filter(Boolean);
}

function parseJsonList(json?: string, fallback?: string): string[] {
    if (json) {
        try {
            const parsed = JSON.parse(json);
            if (Array.isArray(parsed)) {
                return parsed
                    .filter((item): item is string => typeof item === 'string')
                    .map(item => item.trim().toUpperCase())
                    .filter(Boolean);
            }
        } catch {
            // Fall back to the legacy comma list.
        }
    }
    return splitList(fallback);
}

function listToJson(values: string[]): string {
    return JSON.stringify(Array.from(new Set(values.map(value => value.trim().toUpperCase()).filter(Boolean))));
}

function AccessMultiSelect({
    label,
    selected,
    options,
    onChange,
    disabled,
}: {
    label: string;
    selected: string[];
    options: string[];
    onChange: (values: string[]) => void;
    disabled?: boolean;
}) {
    const normalizedOptions = Array.from(new Set([...options, ...selected].map(value => value.toUpperCase()))).sort();

    return (
        <Field label={label} hint="Leave empty to allow all configured values.">
            <Dropdown
                multiselect
                placeholder="All allowed"
                selectedOptions={selected}
                value={selected.length ? selected.join(', ') : 'All allowed'}
                onOptionSelect={(_, data) => onChange(data.selectedOptions.map(value => String(value).toUpperCase()))}
                disabled={disabled}
            >
                {normalizedOptions.map(option => (
                    <Option key={option} value={option} text={option}>
                        {option}
                    </Option>
                ))}
            </Dropdown>
        </Field>
    );
}

const useStyles = makeStyles({
    container: {
        padding: tokens.spacingHorizontalL,
        maxWidth: '1200px',
        margin: '0 auto',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: tokens.spacingVerticalL,
    },
    tabList: {
        marginBottom: tokens.spacingVerticalL,
    },
    card: {
        marginBottom: tokens.spacingVerticalL,
    },
    tableContainer: {
        overflowX: 'auto',
    },
    actionButtons: {
        display: 'flex',
        gap: tokens.spacingHorizontalS,
    },
    formField: {
        marginBottom: tokens.spacingVerticalM,
    },
    inviteUrlContainer: {
        marginTop: tokens.spacingVerticalM,
        padding: tokens.spacingHorizontalM,
        backgroundColor: tokens.colorNeutralBackground3,
        borderRadius: tokens.borderRadiusMedium,
    },
    inviteUrl: {
        fontFamily: 'monospace',
        fontSize: tokens.fontSizeBase200,
        wordBreak: 'break-all',
    },
    statusBadge: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalXS,
    },
    emptyState: {
        textAlign: 'center',
        padding: tokens.spacingVerticalXXL,
        color: tokens.colorNeutralForeground3,
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        padding: tokens.spacingVerticalXXL,
    },
});

interface CreateUserDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: (inviteUrl?: string) => void;
    clients: ClientInfo[];
}

function CreateUserDialog({ open, onClose, onSuccess, clients }: CreateUserDialogProps) {
    const styles = useStyles();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [inviteUrl, setInviteUrl] = useState<string | null>(null);
    const [emailSent, setEmailSent] = useState(false);

    const [formData, setFormData] = useState({
        username: '',
        email: '',
        displayName: '',
        clientId: '',
    });

    const resetForm = () => {
        setFormData({ username: '', email: '', displayName: '', clientId: '' });
        setError(null);
        setInviteUrl(null);
        setEmailSent(false);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError(null);

        try {
            const result = await createUser(formData);
            setInviteUrl(result.inviteUrl);
            setEmailSent(result.emailSent);
            onSuccess(result.inviteUrl);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to create user');
        } finally {
            setLoading(false);
        }
    };

    const copyInviteUrl = () => {
        if (inviteUrl) {
            navigator.clipboard.writeText(inviteUrl);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(_, data) => !data.open && handleClose()}>
            <DialogSurface>
                <DialogBody>
                    <DialogTitle>
                        {inviteUrl ? 'User Created Successfully' : 'Create New User'}
                    </DialogTitle>
                    <DialogContent>
                        {inviteUrl ? (
                            <>
                                <Text>
                                    {emailSent
                                        ? '✅ User created and invite email sent!'
                                        : '⚠️ User created but email sending failed. Share this link manually:'}
                                </Text>
                                <div className={styles.inviteUrlContainer}>
                                    <Text className={styles.inviteUrl}>{inviteUrl}</Text>
                                    <Button
                                        appearance="subtle"
                                        icon={<CopyRegular />}
                                        onClick={copyInviteUrl}
                                        style={{ marginTop: tokens.spacingVerticalS }}
                                    >
                                        Copy Link
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <>
                                {error && (
                                    <Text style={{ color: tokens.colorPaletteRedForeground1, marginBottom: tokens.spacingVerticalM, display: 'block' }}>
                                        {error}
                                    </Text>
                                )}
                                <Field label="Client" required className={styles.formField}>
                                    <Dropdown
                                        placeholder="Select a client..."
                                        selectedOptions={formData.clientId ? [formData.clientId] : []}
                                        onOptionSelect={(_, data) => {
                                            if (data.optionValue) {
                                                setFormData({ ...formData, clientId: data.optionValue as string });
                                            }
                                        }}
                                        disabled={loading}
                                    >
                                        {clients.map(client => (
                                            <Option key={client.clientId} value={client.clientId} text={`${client.displayName} (${client.clientId})`}>
                                                {client.displayName} ({client.clientId})
                                            </Option>
                                        ))}
                                    </Dropdown>
                                </Field>
                                <Field label="Username" required className={styles.formField}>
                                    <Input
                                        value={formData.username}
                                        onChange={(_, data) => setFormData({ ...formData, username: data.value })}
                                        placeholder="e.g., jsmith"
                                        disabled={loading}
                                    />
                                </Field>
                                <Field label="Email" required className={styles.formField}>
                                    <Input
                                        type="email"
                                        value={formData.email}
                                        onChange={(_, data) => setFormData({ ...formData, email: data.value })}
                                        placeholder="john.smith@company.com"
                                        disabled={loading}
                                    />
                                </Field>
                                <Field label="Display Name" required className={styles.formField}>
                                    <Input
                                        value={formData.displayName}
                                        onChange={(_, data) => setFormData({ ...formData, displayName: data.value })}
                                        placeholder="John Smith"
                                        disabled={loading}
                                    />
                                </Field>
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        {inviteUrl ? (
                            <Button appearance="primary" onClick={handleClose}>
                                Done
                            </Button>
                        ) : (
                            <>
                                <Button appearance="secondary" onClick={handleClose} disabled={loading}>
                                    Cancel
                                </Button>
                                <Button
                                    appearance="primary"
                                    onClick={handleSubmit}
                                    disabled={loading || !formData.username || !formData.email || !formData.displayName || !formData.clientId}
                                >
                                    {loading ? <Spinner size="tiny" /> : 'Create & Send Invite'}
                                </Button>
                            </>
                        )}
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
}

interface DeleteUserDialogProps {
    user: AdminUser | null;
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    loading: boolean;
}

function DeleteUserDialog({ user, open, onClose, onConfirm, loading }: DeleteUserDialogProps) {
    return (
        <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
            <DialogSurface>
                <DialogBody>
                    <DialogTitle>Delete User</DialogTitle>
                    <DialogContent>
                        <Text>
                            Are you sure you want to delete user <strong>{user?.username}</strong> ({user?.displayName})?
                            This action cannot be undone.
                        </Text>
                    </DialogContent>
                    <DialogActions>
                        <Button appearance="secondary" onClick={onClose} disabled={loading}>
                            Cancel
                        </Button>
                        <Button appearance="primary" onClick={onConfirm} disabled={loading}>
                            {loading ? <Spinner size="tiny" /> : 'Delete'}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
}

interface EditUserDialogProps {
    user: AdminUser | null;
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    clients: ClientInfo[];
}

function EditUserDialog({ user, open, onClose, onSuccess, clients }: EditUserDialogProps) {
    const styles = useStyles();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [formData, setFormData] = useState({
        email: '',
        displayName: '',
        clientId: '',
    });

    // Update form when user changes
    useEffect(() => {
        if (user) {
            setFormData({
                email: user.email,
                displayName: user.displayName,
                clientId: user.clientId,
            });
        }
    }, [user]);

    const handleClose = () => {
        setError(null);
        onClose();
    };

    const handleSubmit = async () => {
        if (!user) return;

        setLoading(true);
        setError(null);

        try {
            await updateUser(user.username, formData);
            onSuccess();
            handleClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to update user');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(_, data) => !data.open && handleClose()}>
            <DialogSurface>
                <DialogBody>
                    <DialogTitle>Edit User: {user?.username}</DialogTitle>
                    <DialogContent>
                        {error && (
                            <Text style={{ color: tokens.colorPaletteRedForeground1, marginBottom: tokens.spacingVerticalM, display: 'block' }}>
                                {error}
                            </Text>
                        )}
                        <Field label="Client" required className={styles.formField}>
                            <Dropdown
                                placeholder="Select a client..."
                                selectedOptions={formData.clientId ? [formData.clientId] : []}
                                onOptionSelect={(_, data) => {
                                    if (data.optionValue) {
                                        setFormData({ ...formData, clientId: data.optionValue as string });
                                    }
                                }}
                                disabled={loading}
                            >
                                {clients.map(client => (
                                    <Option key={client.clientId} value={client.clientId} text={`${client.displayName} (${client.clientId})`}>
                                        {client.displayName} ({client.clientId})
                                    </Option>
                                ))}
                            </Dropdown>
                        </Field>
                        <Field label="Email" required className={styles.formField}>
                            <Input
                                type="email"
                                value={formData.email}
                                onChange={(_, data) => setFormData({ ...formData, email: data.value })}
                                disabled={loading}
                            />
                        </Field>
                        <Field label="Display Name" required className={styles.formField}>
                            <Input
                                value={formData.displayName}
                                onChange={(_, data) => setFormData({ ...formData, displayName: data.value })}
                                disabled={loading}
                            />
                        </Field>
                    </DialogContent>
                    <DialogActions>
                        <Button appearance="secondary" onClick={handleClose} disabled={loading}>
                            Cancel
                        </Button>
                        <Button
                            appearance="primary"
                            onClick={handleSubmit}
                            disabled={loading || !formData.email || !formData.displayName || !formData.clientId}
                        >
                            {loading ? <Spinner size="tiny" /> : 'Save Changes'}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
}

// ============ Client Dialogs ============

interface CreateClientDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

function CreateClientDialog({ open, onClose, onSuccess }: CreateClientDialogProps) {
    const styles = useStyles();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [catalogDocTypes, setCatalogDocTypes] = useState(DEFAULT_DOC_TYPES);
    const [catalogPartners, setCatalogPartners] = useState(DEFAULT_PARTNER_OPTIONS);
    const [selectedDocTypes, setSelectedDocTypes] = useState<string[]>([]);
    const [selectedPartners, setSelectedPartners] = useState<string[]>([]);

    const [formData, setFormData] = useState({
        clientId: '',
        displayName: '',
        sftpFolder: '',
        allowedDocTypes: '',
        allowedPartners: '',
    });

    const handleClose = () => {
        setFormData({ clientId: '', displayName: '', sftpFolder: '', allowedDocTypes: '', allowedPartners: '' });
        setSelectedDocTypes([]);
        setSelectedPartners([]);
        setError(null);
        onClose();
    };

    useEffect(() => {
        if (!open) return;
        getAccessCatalog()
            .then(catalog => {
                setCatalogDocTypes(catalog.documentTypes.length ? catalog.documentTypes.map(item => item.toUpperCase()) : DEFAULT_DOC_TYPES);
                setCatalogPartners(catalog.partners.length ? catalog.partners.map(item => item.toUpperCase()) : DEFAULT_PARTNER_OPTIONS);
            })
            .catch(() => {
                setCatalogDocTypes(DEFAULT_DOC_TYPES);
                setCatalogPartners(DEFAULT_PARTNER_OPTIONS);
            });
    }, [open]);

    const handleSubmit = async () => {
        setLoading(true);
        setError(null);

        try {
            await createClient({
                clientId: formData.clientId.toUpperCase(),
                displayName: formData.displayName,
                sftpFolder: formData.sftpFolder,
                allowedDocTypes: selectedDocTypes.join(','),
                allowedPartners: selectedPartners.join(','),
                allowedDocTypesJson: selectedDocTypes.length ? listToJson(selectedDocTypes) : '',
                allowedPartnersJson: selectedPartners.length ? listToJson(selectedPartners) : '',
            });
            handleClose();
            onSuccess();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to create client');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(_, data) => !data.open && handleClose()}>
            <DialogSurface>
                <DialogBody>
                    <DialogTitle>Create New Client</DialogTitle>
                    <DialogContent>
                        {error && (
                            <div style={{ color: tokens.colorPaletteRedForeground1, marginBottom: tokens.spacingVerticalM }}>
                                {error}
                            </div>
                        )}
                        <Field label="Client ID" required className={styles.formField} hint="Uppercase identifier (e.g., PPC, NEHI)">
                            <Input
                                value={formData.clientId}
                                onChange={(_, data) => setFormData({ ...formData, clientId: data.value.toUpperCase() })}
                                disabled={loading}
                                placeholder="PPC"
                            />
                        </Field>
                        <Field label="Display Name" required className={styles.formField}>
                            <Input
                                value={formData.displayName}
                                onChange={(_, data) => setFormData({ ...formData, displayName: data.value })}
                                disabled={loading}
                                placeholder="Pharmacy Partners Corporation"
                            />
                        </Field>
                        <Field label="SFTP Folder" required className={styles.formField} hint="Folder name in SFTP storage (e.g., ppc)">
                            <Input
                                value={formData.sftpFolder}
                                onChange={(_, data) => setFormData({ ...formData, sftpFolder: data.value.toLowerCase() })}
                                disabled={loading}
                                placeholder="ppc"
                            />
                        </Field>
                        <AccessMultiSelect
                            label="Allowed Document Types"
                            selected={selectedDocTypes}
                            options={catalogDocTypes}
                            onChange={setSelectedDocTypes}
                            disabled={loading}
                        />
                        <AccessMultiSelect
                            label="Allowed Partners"
                            selected={selectedPartners}
                            options={catalogPartners}
                            onChange={setSelectedPartners}
                            disabled={loading}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button appearance="secondary" onClick={handleClose} disabled={loading}>
                            Cancel
                        </Button>
                        <Button
                            appearance="primary"
                            onClick={handleSubmit}
                            disabled={loading || !formData.clientId || !formData.displayName || !formData.sftpFolder}
                        >
                            {loading ? <Spinner size="tiny" /> : 'Create Client'}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
}

interface EditClientDialogProps {
    client: ClientInfo | null;
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

function EditClientDialog({ client, open, onClose, onSuccess }: EditClientDialogProps) {
    const styles = useStyles();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [catalogDocTypes, setCatalogDocTypes] = useState(DEFAULT_DOC_TYPES);
    const [catalogPartners, setCatalogPartners] = useState(DEFAULT_PARTNER_OPTIONS);
    const [selectedDocTypes, setSelectedDocTypes] = useState<string[]>([]);
    const [selectedPartners, setSelectedPartners] = useState<string[]>([]);

    const [formData, setFormData] = useState({
        displayName: '',
        sftpFolder: '',
        isActive: true,
        allowedDocTypes: '',
        allowedPartners: '',
    });

    useEffect(() => {
        if (client) {
            setFormData({
                displayName: client.displayName,
                sftpFolder: client.sftpFolder,
                isActive: client.isActive,
                allowedDocTypes: client.allowedDocTypes || '',
                allowedPartners: client.allowedPartners || '',
            });
            setSelectedDocTypes(parseJsonList(client.allowedDocTypesJson, client.allowedDocTypes));
            setSelectedPartners(parseJsonList(client.allowedPartnersJson, client.allowedPartners));
        }
    }, [client]);

    useEffect(() => {
        if (!open) return;
        getAccessCatalog(client?.clientId)
            .then(catalog => {
                setCatalogDocTypes(catalog.documentTypes.length ? catalog.documentTypes.map(item => item.toUpperCase()) : DEFAULT_DOC_TYPES);
                setCatalogPartners(catalog.partners.length ? catalog.partners.map(item => item.toUpperCase()) : DEFAULT_PARTNER_OPTIONS);
            })
            .catch(() => {
                setCatalogDocTypes(DEFAULT_DOC_TYPES);
                setCatalogPartners(DEFAULT_PARTNER_OPTIONS);
            });
    }, [client?.clientId, open]);

    const handleClose = () => {
        setError(null);
        onClose();
    };

    const handleSubmit = async () => {
        if (!client) return;
        setLoading(true);
        setError(null);

        try {
            await updateClient(client.clientId, {
                displayName: formData.displayName,
                sftpFolder: formData.sftpFolder,
                isActive: formData.isActive,
                allowedDocTypes: selectedDocTypes.join(','),
                allowedPartners: selectedPartners.join(','),
                allowedDocTypesJson: selectedDocTypes.length ? listToJson(selectedDocTypes) : '',
                allowedPartnersJson: selectedPartners.length ? listToJson(selectedPartners) : '',
            });
            handleClose();
            onSuccess();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to update client');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(_, data) => !data.open && handleClose()}>
            <DialogSurface>
                <DialogBody>
                    <DialogTitle>Edit Client: {client?.clientId}</DialogTitle>
                    <DialogContent>
                        {error && (
                            <div style={{ color: tokens.colorPaletteRedForeground1, marginBottom: tokens.spacingVerticalM }}>
                                {error}
                            </div>
                        )}
                        <Field label="Display Name" required className={styles.formField}>
                            <Input
                                value={formData.displayName}
                                onChange={(_, data) => setFormData({ ...formData, displayName: data.value })}
                                disabled={loading}
                            />
                        </Field>
                        <Field label="SFTP Folder" required className={styles.formField} hint="Folder name in SFTP storage">
                            <Input
                                value={formData.sftpFolder}
                                onChange={(_, data) => setFormData({ ...formData, sftpFolder: data.value.toLowerCase() })}
                                disabled={loading}
                            />
                        </Field>
                        <AccessMultiSelect
                            label="Allowed Document Types"
                            selected={selectedDocTypes}
                            options={catalogDocTypes}
                            onChange={setSelectedDocTypes}
                            disabled={loading}
                        />
                        <AccessMultiSelect
                            label="Allowed Trading Partners"
                            selected={selectedPartners}
                            options={catalogPartners}
                            onChange={setSelectedPartners}
                            disabled={loading}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button appearance="secondary" onClick={handleClose} disabled={loading}>
                            Cancel
                        </Button>
                        <Button
                            appearance="primary"
                            onClick={handleSubmit}
                            disabled={loading || !formData.displayName || !formData.sftpFolder}
                        >
                            {loading ? <Spinner size="tiny" /> : 'Save Changes'}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
}

interface DeleteClientDialogProps {
    client: ClientInfo | null;
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    loading: boolean;
}

function DeleteClientDialog({ client, open, onClose, onConfirm, loading }: DeleteClientDialogProps) {
    return (
        <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
            <DialogSurface>
                <DialogBody>
                    <DialogTitle>Delete Client</DialogTitle>
                    <DialogContent>
                        <Text>
                            Are you sure you want to delete client <strong>{client?.displayName}</strong> ({client?.clientId})?
                        </Text>
                        <Text style={{ display: 'block', marginTop: tokens.spacingVerticalM, color: tokens.colorPaletteRedForeground1 }}>
                            Warning: This will not delete users associated with this client.
                        </Text>
                    </DialogContent>
                    <DialogActions>
                        <Button appearance="secondary" onClick={onClose} disabled={loading}>
                            Cancel
                        </Button>
                        <Button appearance="primary" onClick={onConfirm} disabled={loading}>
                            {loading ? <Spinner size="tiny" /> : 'Delete Client'}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
}

export function AdminPage() {
    const styles = useStyles();
    const navigate = useNavigate();
    const toasterId = useId('toaster');
    const { dispatchToast } = useToastController(toasterId);

    // Tab state
    const [activeTab, setActiveTab] = useState<'users' | 'clients'>('users');

    // Users state
    const [users, setUsers] = useState<AdminUser[]>([]);
    const [usersLoading, setUsersLoading] = useState(true);
    const [usersError, setUsersError] = useState<string | null>(null);

    // Clients state
    const [clients, setClients] = useState<ClientInfo[]>([]);
    const [clientsLoading, setClientsLoading] = useState(true);
    const [clientsError, setClientsError] = useState<string | null>(null);

    // User dialog states
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState<AdminUser | null>(null);
    const [userToEdit, setUserToEdit] = useState<AdminUser | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [resendingInvite, setResendingInvite] = useState<string | null>(null);

    // Client dialog states
    const [createClientDialogOpen, setCreateClientDialogOpen] = useState(false);
    const [editClientDialogOpen, setEditClientDialogOpen] = useState(false);
    const [deleteClientDialogOpen, setDeleteClientDialogOpen] = useState(false);
    const [clientToEdit, setClientToEdit] = useState<ClientInfo | null>(null);
    const [clientToDelete, setClientToDelete] = useState<ClientInfo | null>(null);
    const [deleteClientLoading, setDeleteClientLoading] = useState(false);

    const loadUsers = useCallback(async () => {
        setUsersLoading(true);
        setUsersError(null);
        try {
            const data = await listAllUsers();
            setUsers(data);
        } catch (err) {
            setUsersError(err instanceof Error ? err.message : 'Failed to load users');
        } finally {
            setUsersLoading(false);
        }
    }, []);

    const loadClients = useCallback(async () => {
        setClientsLoading(true);
        setClientsError(null);
        try {
            const data = await listClients();
            setClients(data);
        } catch (err) {
            setClientsError(err instanceof Error ? err.message : 'Failed to load clients');
        } finally {
            setClientsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadUsers();
        loadClients();
    }, [loadUsers, loadClients]);

    const handleCreateSuccess = () => {
        loadUsers();
        dispatchToast(
            <Toast>
                <ToastTitle>User Created</ToastTitle>
                <ToastBody>New user has been created and invited.</ToastBody>
            </Toast>,
            { intent: 'success' }
        );
    };

    const handleDeleteClick = (user: AdminUser) => {
        setUserToDelete(user);
        setDeleteDialogOpen(true);
    };

    const handleEditClick = (user: AdminUser) => {
        setUserToEdit(user);
        setEditDialogOpen(true);
    };

    const handleEditSuccess = () => {
        loadUsers();
        dispatchToast(
            <Toast>
                <ToastTitle>User Updated</ToastTitle>
                <ToastBody>User details have been updated.</ToastBody>
            </Toast>,
            { intent: 'success' }
        );
    };

    const handleDeleteConfirm = async () => {
        if (!userToDelete) return;

        setDeleteLoading(true);
        try {
            await deleteUser(userToDelete.username);
            await loadUsers();
            setDeleteDialogOpen(false);
            setUserToDelete(null);
            dispatchToast(
                <Toast>
                    <ToastTitle>User Deleted</ToastTitle>
                    <ToastBody>User {userToDelete.username} has been deleted.</ToastBody>
                </Toast>,
                { intent: 'success' }
            );
        } catch (err) {
            dispatchToast(
                <Toast>
                    <ToastTitle>Delete Failed</ToastTitle>
                    <ToastBody>{err instanceof Error ? err.message : 'Failed to delete user'}</ToastBody>
                </Toast>,
                { intent: 'error' }
            );
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleResendInvite = async (user: AdminUser) => {
        setResendingInvite(user.username);
        try {
            const result = await resendInvite(user.username, user.email);
            if (result.emailSent) {
                dispatchToast(
                    <Toast>
                        <ToastTitle>Invite Sent</ToastTitle>
                        <ToastBody>Invite email resent to {user.email}</ToastBody>
                    </Toast>,
                    { intent: 'success' }
                );
            } else {
                // Copy to clipboard if email failed
                if (result.inviteUrl) {
                    await navigator.clipboard.writeText(result.inviteUrl);
                    dispatchToast(
                        <Toast>
                            <ToastTitle>Email Failed - Link Copied</ToastTitle>
                            <ToastBody>Email sending failed. Invite link copied to clipboard.</ToastBody>
                        </Toast>,
                        { intent: 'warning' }
                    );
                }
            }
        } catch (err) {
            dispatchToast(
                <Toast>
                    <ToastTitle>Resend Failed</ToastTitle>
                    <ToastBody>{err instanceof Error ? err.message : 'Failed to resend invite'}</ToastBody>
                </Toast>,
                { intent: 'error' }
            );
        } finally {
            setResendingInvite(null);
        }
    };

    const formatDate = (dateStr: string | undefined) => {
        if (!dateStr) return 'Never';
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    // Client handlers
    const handleCreateClientSuccess = () => {
        loadClients();
        dispatchToast(
            <Toast>
                <ToastTitle>Client Created</ToastTitle>
                <ToastBody>New client has been created.</ToastBody>
            </Toast>,
            { intent: 'success' }
        );
    };

    const handleEditClientClick = (client: ClientInfo) => {
        setClientToEdit(client);
        setEditClientDialogOpen(true);
    };

    const handleEditClientSuccess = () => {
        loadClients();
        dispatchToast(
            <Toast>
                <ToastTitle>Client Updated</ToastTitle>
                <ToastBody>Client has been updated.</ToastBody>
            </Toast>,
            { intent: 'success' }
        );
    };

    const handleDeleteClientClick = (client: ClientInfo) => {
        setClientToDelete(client);
        setDeleteClientDialogOpen(true);
    };

    const handleDeleteClientConfirm = async () => {
        if (!clientToDelete) return;

        setDeleteClientLoading(true);
        try {
            await deleteClient(clientToDelete.clientId);
            await loadClients();
            setDeleteClientDialogOpen(false);
            setClientToDelete(null);
            dispatchToast(
                <Toast>
                    <ToastTitle>Client Deleted</ToastTitle>
                    <ToastBody>Client {clientToDelete.displayName} has been deleted.</ToastBody>
                </Toast>,
                { intent: 'success' }
            );
        } catch (err) {
            dispatchToast(
                <Toast>
                    <ToastTitle>Delete Failed</ToastTitle>
                    <ToastBody>{err instanceof Error ? err.message : 'Failed to delete client'}</ToastBody>
                </Toast>,
                { intent: 'error' }
            );
        } finally {
            setDeleteClientLoading(false);
        }
    };

    return (
        <div className={styles.container}>
            <Toaster toasterId={toasterId} />

            <div className={styles.header}>
                <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>
                    <Button
                        appearance="subtle"
                        icon={<ArrowLeftRegular />}
                        onClick={() => navigate('/')}
                    >
                        Back to Portal
                    </Button>
                    <h1 style={{ margin: 0 }}>Administration</h1>
                </div>
                {activeTab === 'users' ? (
                    <Button
                        appearance="primary"
                        icon={<PersonAddRegular />}
                        onClick={() => setCreateDialogOpen(true)}
                    >
                        Create User
                    </Button>
                ) : (
                    <Button
                        appearance="primary"
                        icon={<AddRegular />}
                        onClick={() => setCreateClientDialogOpen(true)}
                    >
                        Create Client
                    </Button>
                )}
            </div>

            <TabList
                className={styles.tabList}
                selectedValue={activeTab}
                onTabSelect={(_, data) => setActiveTab(data.value as 'users' | 'clients')}
            >
                <Tab value="users" icon={<PersonAddRegular />}>Users</Tab>
                <Tab value="clients" icon={<BuildingRegular />}>Clients</Tab>
            </TabList>

            {activeTab === 'users' && (
                <Card className={styles.card}>
                    <CardHeader header={<Text weight="semibold">All Users</Text>} />

                    {usersLoading ? (
                        <div className={styles.loadingContainer}>
                            <Spinner label="Loading users..." />
                        </div>
                    ) : usersError ? (
                        <div className={styles.emptyState}>
                            <Text style={{ color: tokens.colorPaletteRedForeground1 }}>{usersError}</Text>
                            <Button appearance="subtle" icon={<ArrowSyncRegular />} onClick={loadUsers}>
                                Retry
                            </Button>
                        </div>
                    ) : users.length === 0 ? (
                        <div className={styles.emptyState}>
                            <Text>No users found. Create your first user to get started.</Text>
                        </div>
                    ) : (
                        <div className={styles.tableContainer}>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHeaderCell>Username</TableHeaderCell>
                                        <TableHeaderCell>Display Name</TableHeaderCell>
                                        <TableHeaderCell>Email</TableHeaderCell>
                                        <TableHeaderCell>Client</TableHeaderCell>
                                        <TableHeaderCell>Status</TableHeaderCell>
                                        <TableHeaderCell>Last Login</TableHeaderCell>
                                        <TableHeaderCell>Actions</TableHeaderCell>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {users.map((user) => (
                                        <TableRow key={user.username}>
                                            <TableCell>{user.username}</TableCell>
                                            <TableCell>{user.displayName}</TableCell>
                                            <TableCell>{user.email}</TableCell>
                                            <TableCell>
                                                <Badge appearance="outline">{user.clientId}</Badge>
                                            </TableCell>
                                            <TableCell>
                                                <div className={styles.statusBadge}>
                                                    {user.pendingInvite ? (
                                                        <Badge appearance="tint" color="warning" icon={<ClockRegular />}>
                                                            Pending Invite
                                                        </Badge>
                                                    ) : user.isActive ? (
                                                        <Badge appearance="tint" color="success" icon={<CheckmarkCircleRegular />}>
                                                            Active
                                                        </Badge>
                                                    ) : (
                                                        <Badge appearance="tint" color="danger">
                                                            Inactive
                                                        </Badge>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>{formatDate(user.lastLogin)}</TableCell>
                                            <TableCell>
                                                <div className={styles.actionButtons}>
                                                    <Tooltip content="Edit User" relationship="label">
                                                        <Button
                                                            appearance="subtle"
                                                            icon={<EditRegular />}
                                                            onClick={() => handleEditClick(user)}
                                                        />
                                                    </Tooltip>
                                                    {user.pendingInvite && (
                                                        <Tooltip content="Resend Invite" relationship="label">
                                                            <Button
                                                                appearance="subtle"
                                                                icon={resendingInvite === user.username ? <Spinner size="tiny" /> : <MailRegular />}
                                                                onClick={() => handleResendInvite(user)}
                                                                disabled={resendingInvite === user.username}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip content="Delete User" relationship="label">
                                                        <Button
                                                            appearance="subtle"
                                                            icon={<DeleteRegular />}
                                                            onClick={() => handleDeleteClick(user)}
                                                        />
                                                    </Tooltip>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </Card>
            )}

            {activeTab === 'clients' && (
                <Card className={styles.card}>
                    <CardHeader header={<Text weight="semibold">All Clients</Text>} />

                    {clientsLoading ? (
                        <div className={styles.loadingContainer}>
                            <Spinner label="Loading clients..." />
                        </div>
                    ) : clientsError ? (
                        <div className={styles.emptyState}>
                            <Text style={{ color: tokens.colorPaletteRedForeground1 }}>{clientsError}</Text>
                            <Button appearance="subtle" icon={<ArrowSyncRegular />} onClick={loadClients}>
                                Retry
                            </Button>
                        </div>
                    ) : clients.length === 0 ? (
                        <div className={styles.emptyState}>
                            <Text>No clients found. Create your first client to get started.</Text>
                        </div>
                    ) : (
                        <div className={styles.tableContainer}>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHeaderCell>Client ID</TableHeaderCell>
                                        <TableHeaderCell>Display Name</TableHeaderCell>
                                        <TableHeaderCell>SFTP Folder</TableHeaderCell>
                                        <TableHeaderCell>Status</TableHeaderCell>
                                        <TableHeaderCell>Created</TableHeaderCell>
                                        <TableHeaderCell>Actions</TableHeaderCell>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {clients.map((client) => (
                                        <TableRow key={client.clientId}>
                                            <TableCell>
                                                <Badge appearance="filled" color="brand">{client.clientId}</Badge>
                                            </TableCell>
                                            <TableCell>{client.displayName}</TableCell>
                                            <TableCell>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS }}>
                                                    <FolderRegular />
                                                    <code>{client.sftpFolder}</code>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {client.isActive ? (
                                                    <Badge appearance="tint" color="success" icon={<CheckmarkCircleRegular />}>
                                                        Active
                                                    </Badge>
                                                ) : (
                                                    <Badge appearance="tint" color="danger">
                                                        Inactive
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            <TableCell>{formatDate(client.createdAt)}</TableCell>
                                            <TableCell>
                                                <div className={styles.actionButtons}>
                                                    <Tooltip content="Edit Client" relationship="label">
                                                        <Button
                                                            appearance="subtle"
                                                            icon={<EditRegular />}
                                                            onClick={() => handleEditClientClick(client)}
                                                        />
                                                    </Tooltip>
                                                    <Tooltip content="Delete Client" relationship="label">
                                                        <Button
                                                            appearance="subtle"
                                                            icon={<DeleteRegular />}
                                                            onClick={() => handleDeleteClientClick(client)}
                                                        />
                                                    </Tooltip>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </Card>
            )}

            {/* User Dialogs */}
            <CreateUserDialog
                open={createDialogOpen}
                onClose={() => setCreateDialogOpen(false)}
                onSuccess={handleCreateSuccess}
                clients={clients}
            />

            <DeleteUserDialog
                user={userToDelete}
                open={deleteDialogOpen}
                onClose={() => {
                    setDeleteDialogOpen(false);
                    setUserToDelete(null);
                }}
                onConfirm={handleDeleteConfirm}
                loading={deleteLoading}
            />

            <EditUserDialog
                user={userToEdit}
                open={editDialogOpen}
                onClose={() => {
                    setEditDialogOpen(false);
                    setUserToEdit(null);
                }}
                onSuccess={handleEditSuccess}
                clients={clients}
            />

            {/* Client Dialogs */}
            <CreateClientDialog
                open={createClientDialogOpen}
                onClose={() => setCreateClientDialogOpen(false)}
                onSuccess={handleCreateClientSuccess}
            />

            <EditClientDialog
                client={clientToEdit}
                open={editClientDialogOpen}
                onClose={() => {
                    setEditClientDialogOpen(false);
                    setClientToEdit(null);
                }}
                onSuccess={handleEditClientSuccess}
            />

            <DeleteClientDialog
                client={clientToDelete}
                open={deleteClientDialogOpen}
                onClose={() => {
                    setDeleteClientDialogOpen(false);
                    setClientToDelete(null);
                }}
                onConfirm={handleDeleteClientConfirm}
                loading={deleteClientLoading}
            />
        </div>
    );
}
